<template>
  <div class="report-layout">
    <TopBar class="report-layout__top-bar" />
    <SideBar />
    <main
      :class="{
        'report-layout__main': true,
        'report-layout__main--minimized': isSideBarMinimized,
      }"
    >
      <div v-if="hasTitle" data-cy="report-layout-title" class="report-layout__title-container">
        <div class="report-layout__title-row">
          <UgIcon
            v-if="titleIcon"
            :class="{
              'report-layout__title-icon': true,
              'report-layout__title-icon--branches': titleIcon === 'branches',
            }"
            :icon="titleIcon"
            width="16"
            height="16"
          />
          <h1>{{ title }}</h1>
        </div>
      </div>

      <slot></slot>
      <BlogLinkContainer />
    </main>
  </div>
</template>

<script setup lang="ts">
import { UgIcon } from "@bebit/atomic";
import BlogLinkContainer from "search/BlogLinkContainer.vue";
import { computed } from "vue";
import { useRoute } from "vue-router";
import SideBar from "@host/components/SideBar.vue";
import TopBar from "@host/components/TopBar.vue";
import { sideBarStore } from "@host/store/side-bar";
import type { Icon } from "@bebit/atomic";

const route = useRoute();

const title = computed(() => route?.meta?.title || "");
const titleIcon = computed(() => (route?.meta?.icon as Icon) || "");

const { isSideBarMinimized } = sideBarStore();

interface Props {
  hasTitle?: boolean;
}

withDefaults(defineProps<Props>(), {
  hasTitle: true,
});
</script>

<style lang="scss">
.report-layout {
  display: grid;
  grid-template-columns: 0 auto;
  grid-template-rows: auto auto;
  line-height: normal;
}

.report-layout__top-bar {
  grid-column: 1 / 3;
  z-index: map.get($z-indexes, "top-bar");
}

.report-layout__main {
  margin-left: $sideNavigationExpandedWidth;

  &--minimized {
    margin-left: $sideNavigationMinimizedWidth;
  }
}

@media print {
  .report-layout__main {
    margin: 0;
  }
}

.report-layout__title-container {
  height: 53px;
  color: $colorGray1000;
  display: flex;
  justify-content: center;
}

.report-layout__title-row {
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

$titleIconColor: $colorBase900;

.report-layout__title-icon:not(.report-layout__title-icon--branches) {
  path {
    fill: $titleIconColor;
  }
}

.report-layout__title-icon.report-layout__title-icon--branches {
  path:not(:first-child):not(:last-child) {
    fill: $titleIconColor;
  }
}
</style>
